import { FormProps } from '@alphabait/ui/form'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Plus, PlusCircle, Trash2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useController, Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui'
import { AlphaTypes, CustomShadcnSelect } from './custom-types'

type SelectCreatableOption = {
  label: string
  value: string
  data?: unknown
  disabled?: boolean
  default?: boolean
}

// Legacy mode props (used with AlphaForm)
type LegacySelectProps = {
  field: CustomShadcnSelect
  formProps: FormProps
}

// Direct mode props (used with plain react-hook-form)
type DirectSelectProps = {
  name: string
  control: Control<any>
  options: SelectCreatableOption[]
  creatable?: boolean
  disabled?: boolean
  placeholder?: string
}

// Discriminated union - component accepts either legacy or direct props
type CustomSelectProps = LegacySelectProps | DirectSelectProps

function isLegacySelectProps(props: CustomSelectProps): props is LegacySelectProps {
  return 'field' in props && 'formProps' in props
}

export function CustomShadcnSelectComponent(props: CustomSelectProps) {
  const { t } = useTranslation()

  // Extract the required values from either legacy or direct props
  const fieldName = isLegacySelectProps(props) ? props.field.name : props.name
  const control = isLegacySelectProps(props) ? props.formProps.control : props.control
  const initialOptions = isLegacySelectProps(props) ? props.field.options : props.options
  const creatable = isLegacySelectProps(props) ? props.field.creatable : props.creatable
  const disabled = isLegacySelectProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const placeholder = isLegacySelectProps(props)
    ? props.field.uiOptions?.placeholder
    : props.placeholder

  const [options, setOptions] = useState<SelectCreatableOption[]>(initialOptions)
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined,
  )

  useEffect(() => {
    setOptions(initialOptions)
  }, [initialOptions])

  const CustomOption = (props: { setValue: (value: string) => void }) => {
    const [value, setValue] = useState('')
    const onAdd = () => {
      if (!value) return
      const newValue = {
        label: value,
        value: value,
      }
      setOptions((prevOptions) => [...(prevOptions || []), newValue])
      props.setValue(value)
      setValue('')
    }
    return (
      <div
        className="px-3 py-2 flex w-full items-center space-x-2 border-t border-base-input bg-background-light"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={value}
          placeholder="Add new option..."
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          className="flex-1 h-8 items-center rounded-md border border-base-input bg-background-light px-2 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        <button
          className="inline-flex border border-base-input h-8 items-center justify-center rounded-md px-3 text-xs font-medium text-slate-50 hover:bg-primary/90"
          onClick={() => onAdd()}
        >
          <Plus className="h-3 w-3 mr-1" />
          Add
        </button>
      </div>
    )
  }

  const {
    field: { onChange, value, ref },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  React.useEffect(() => {
    setSelectedValue(value)
  }, [value])

  const handleValueChange = (newValue: string) => {
    setSelectedValue(newValue)
    onChange(newValue)
  }

  return (
    <div>
      <Select
        onValueChange={handleValueChange}
        value={value}
        disabled={disabled}
      >
        <SelectTrigger
          ref={ref}
          className="w-full border !border-base-input !text-slate-50 !bg-background-light"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent className="max-h-[300px] items-center border border-base-input !text-slate-50 !bg-background-light p-0 overflow-y-auto rounded-md shadow-lg">
          <div className=" ">
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={`flex items-center px-3 py-2 cursor-pointer transition-colors !text-slate-50 !bg-background-light  ${!option.disabled
                  ? 'hover:!bg-primary hover:text-white rounded-md'
                  : 'opacity-50 cursor-not-allowed'
                  } ${selectedValue === option.value ? 'bg-primary/20 rounded-md' : ''
                  }`}
              >
                <div className="flex-1">{option.label}</div>
              </SelectItem>
            ))}
          </div>

          {creatable && (
            <CustomOption
              setValue={(newValue) => {
                setOptions([...options, { value: newValue, label: newValue }])
                onChange(newValue)
                setSelectedValue(newValue)
              }}
            />
          )}
        </SelectContent>
      </Select>
      {error && (
        <p className="text-primary text-sm mt-1">
          {t(`validation_messages.${error.message}`, { defaultValue: error.message })}
        </p>
      )}
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const getCustomShadcnSelectProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'options',
      type: 'render',
      validations: ['required'],
      render: (field: CustomShadcnSelect, formProps: FormProps) => {
        return <DropdownOptions field={field} formProps={formProps} />
      },
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]

function DropdownOptions({
  field,
  formProps,
}: {
  field: CustomShadcnSelect
  formProps: FormProps
}) {
  const {
    field: { onChange, value },
  } = useController({
    control: formProps.control,
    name: field.name,
  })

  const [options, setOptions] = useState<CustomShadcnSelect['options']>([])

  useEffect(() => {
    value && setOptions(value)
  }, [value])

  const [inputValue, setInputValue] = useState('')

  const addOption = () => {
    if (!inputValue.trim()) return
    const newOption = { value: inputValue, label: inputValue }
    const newOptions = [...options, newOption]
    setOptions(newOptions)
    onChange(newOptions)
    setInputValue('')
  }

  const updateOption = (index: number, newValue: string) => {
    if (!newValue.trim()) return
    const newOptions = [...options]
    newOptions[index] = {
      ...newOptions[index],
      value: newValue,
      label: newValue,
    }
    setOptions(newOptions)
    onChange(newOptions)
  }

  const removeOption = (index: number) => {
    const newOptions = options.filter((_, i) => i !== index)
    setOptions(newOptions)
    onChange(newOptions)
  }

  const moveOption = (dragIndex: number, hoverIndex: number) => {
    const newOptions = arrayMove(options, dragIndex, hoverIndex)
    setOptions(newOptions)
    onChange(newOptions)
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  return (
    <div className="space-y-6 mt-6">
      <div className="font-medium text-base">Answers</div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event: DragEndEvent) => {
          const { active, over } = event
          if (active.id !== over?.id) {
            const oldIndex = options.findIndex(
              (option) => option.value === active.id,
            )
            const newIndex = options.findIndex(
              (option) => option.value === over?.id,
            )
            moveOption(oldIndex, newIndex)
          }
        }}
      >
        <SortableContext
          items={options.map((option) => option.value)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {options.map((option, index) => (
              <DraggableOptionInput
                key={option.value}
                index={index}
                option={option}
                updateOption={(value) => updateOption(index, value)}
                removeOption={() => removeOption(index)}
              />
            ))}

            {/* Always show an empty field at the end */}
            <div className="flex gap-2 pl-2 items-center">
              <div className="py-1 cursor-not-allowed opacity-40">
                <GripVertical className="h-10 w-4 text-gray-400" />
              </div>
              <div className="flex items-center rounded-md flex-1">
                <OptionInput
                  value={inputValue}
                  onChange={setInputValue}
                  placeholder="Add option"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && inputValue.trim()) {
                      e.preventDefault()
                      addOption()
                    }
                  }}
                />
              </div>
              <button
                type="button"
                onClick={addOption}
                className={`px-1 py-2 hover:bg-gray-100 hover:rounded-full ${
                  !inputValue.trim() ? 'opacity-30 cursor-not-allowed' : ''
                }`}
              >
                <PlusCircle className="h-5 w-5 text-gray-400" />
              </button>
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

function OptionInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = 'Option',
}: {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  placeholder?: string
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className="w-full px-2 py-2 border border-base-input bg-background-light text-slate-50 rounded-md outline-none focus:ring-0 text-sm"
    />
  )
}

function DraggableOptionInput({
  option,
  updateOption,
  removeOption,
}: {
  index: number
  option: SelectCreatableOption
  updateOption: (value: string) => void
  removeOption: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value })

  const [value, setValue] = useState(option.label)

  useEffect(() => {
    setValue(option.label)
  }, [option.label])

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div className="flex gap-2">
      <div
        className="cursor-grab touch-none pl-2 "
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-10 w-4 text-gray-400" />
      </div>
      <div
        ref={setNodeRef}
        style={style}
        className="flex items-center rounded-md h-inherit"
      >
        <OptionInput
          value={value}
          onChange={setValue}
          onBlur={() => {
            if (value.trim() !== option.label) {
              updateOption(value)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (value.trim() !== option.label) {
                updateOption(value)
              }
            }
          }}
        />
      </div>
      <button
        type="button"
        onClick={removeOption}
        className="px-1 py-2 hover:bg-gray-100 hover:rounded-full"
      >
        <Trash2 className="h-5 w-5 text-gray-400" />
      </button>
    </div>
  )
}
